// client/src/components/pages/EmailReports/ReportConfigModal.tsx (converted from .js, ST-00229)
//
// Create / edit a Quick (basic) email report: settings, schedule, preview. RPT-11 (ST-00203):
// unticking "Enable automatic scheduling" now switches an active schedule off, the stored
// "HH:MM:SS" time shows as HH:MM (was NaN:NaN), a failed schedule call is shown, and the page
// receives the report with the saved schedule merged in (see ./scheduleUtils).
import { useState, useEffect } from 'react';
import type { IconType } from 'react-icons';
import { toast } from 'react-toastify';
import {
  FaTimes,
  FaPlus,
  FaTrash,
  FaArrowLeft,
  FaArrowRight,
  FaEye,
  FaClock,
  FaCheck,
  FaSpinner,
} from 'react-icons/fa';
import api from '../../../services/api';
import ThemedSelect from '../../common/ThemedSelect';
import ThemedTimePicker from '../../common/ThemedTimePicker';
import { mergeScheduleIntoReport, saveReportSchedule, toScheduleTimeInput } from './scheduleUtils';
import type {
  EmailReportApiError,
  EmailReportDetail,
  EmailReportGroupingOption,
  EmailReportGroupingRef,
  EmailReportListRow,
  EmailReportPreview,
  ScheduleFormData,
  ScheduleType,
  SharePointLink,
} from '../../../types/clientPages/emailReports';

interface ReportFormData {
  reportName: string;
  description: string;
  dateRangeType: string;
  surveyMonthStartDay: number;
  recipients: string[];
  routeGroupings: EmailReportGroupingRef[];
  sharePointLinks: SharePointLink[];
  isActive: boolean;
}

interface ReportConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The report being edited (GET /email-reports/:id); null/absent = create. */
  report?: EmailReportDetail | null;
  /** The saved list row, with the saved schedule merged in. */
  onSave: (report: EmailReportListRow) => void;
}

interface StepDef {
  number: number;
  title: string;
  icon: IconType;
}

const ReportConfigModal = ({ isOpen, onClose, report = null, onSave }: ReportConfigModalProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<ReportFormData>({
    reportName: '',
    description: '',
    dateRangeType: 'month_to_yesterday',
    surveyMonthStartDay: 25,
    recipients: [''],
    routeGroupings: [],
    sharePointLinks: [{ url: '', text: '' }],
    isActive: true,
  });

  const [scheduleData, setScheduleData] = useState<ScheduleFormData>({
    scheduleType: 'daily',
    scheduleTime: '08:00',
    timezone: 'Africa/Johannesburg',
    isActive: false,
  });

  const [previewData, setPreviewData] = useState<EmailReportPreview | null>(null);
  const [availableGroupings, setAvailableGroupings] = useState<EmailReportGroupingOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const steps: StepDef[] = [
    { number: 1, title: 'Report Configuration', icon: FaCheck },
    { number: 2, title: 'Schedule Settings', icon: FaClock },
    { number: 3, title: 'Preview & Confirm', icon: FaEye },
  ];

  // Reset modal state when opening/closing
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setError(null);
      setPreviewData(null);
      loadRouteGroupings();

      if (report) {
        // Edit mode - populate form with existing data
        setFormData({
          reportName: report.name || '',
          description: report.description || '',
          dateRangeType: report.date_range_type || 'month_to_yesterday',
          surveyMonthStartDay: report.survey_month_start_day || 25,
          recipients: report.recipients && report.recipients.length > 0 ? report.recipients : [''],
          routeGroupings: report.routeGroupings || [],
          sharePointLinks:
            report.sharePointLinks && report.sharePointLinks.length > 0
              ? report.sharePointLinks
              : [{ url: '', text: '' }],
          isActive: report.is_active !== undefined ? report.is_active : true,
        });

        // Load existing schedule if available. schedule_time is pg TIME "HH:MM:SS"
        // (`new Date()` of it is an Invalid Date: the form showed NaN:NaN, RPT-11).
        if (report.schedule) {
          setScheduleData({
            scheduleType: (report.schedule.schedule_type as ScheduleType) || 'daily',
            scheduleTime: toScheduleTimeInput(report.schedule.schedule_time),
            timezone: report.schedule.timezone || 'Africa/Johannesburg',
            isActive: report.schedule.is_active || false,
          });
        }
      } else {
        // Create mode - reset form
        setFormData({
          reportName: '',
          description: '',
          dateRangeType: 'month_to_yesterday',
          surveyMonthStartDay: 25,
          recipients: [''],
          routeGroupings: [],
          sharePointLinks: [{ url: '', text: '' }],
          isActive: true,
        });
        setScheduleData({
          scheduleType: 'daily',
          scheduleTime: '08:00',
          timezone: 'Africa/Johannesburg',
          isActive: false,
        });
      }
    }
  }, [isOpen, report]);

  const loadRouteGroupings = async () => {
    try {
      const response = await api.emailReports.getRouteGroupings();
      setAvailableGroupings(response.data as EmailReportGroupingOption[]);
      setError(null);
    } catch (err) {
      console.error('Error loading route groupings:', err);
      const e = err as EmailReportApiError;
      const errorMessage = e.response?.data?.error || e.message || 'Failed to load route groupings';
      setError(`Failed to load groupings: ${errorMessage}`);
    }
  };

  const handleInputChange = <K extends keyof ReportFormData>(
    field: K,
    value: ReportFormData[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleScheduleChange = <K extends keyof ScheduleFormData>(
    field: K,
    value: ScheduleFormData[K]
  ) => {
    setScheduleData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRecipientChange = (index: number, value: string) => {
    const newRecipients = [...formData.recipients];
    newRecipients[index] = value;
    setFormData((prev) => ({
      ...prev,
      recipients: newRecipients,
    }));
  };

  const addRecipient = () => {
    setFormData((prev) => ({
      ...prev,
      recipients: [...prev.recipients, ''],
    }));
  };

  const removeRecipient = (index: number) => {
    if (formData.recipients.length > 1) {
      const newRecipients = formData.recipients.filter((_, i) => i !== index);
      setFormData((prev) => ({
        ...prev,
        recipients: newRecipients,
      }));
    }
  };

  const handleSharePointLinkChange = (
    index: number,
    field: keyof SharePointLink,
    value: string
  ) => {
    const newLinks = [...formData.sharePointLinks];
    newLinks[index] = { ...newLinks[index]!, [field]: value };
    setFormData((prev) => ({
      ...prev,
      sharePointLinks: newLinks,
    }));
  };

  const addSharePointLink = () => {
    setFormData((prev) => ({
      ...prev,
      sharePointLinks: [...prev.sharePointLinks, { url: '', text: '' }],
    }));
  };

  const removeSharePointLink = (index: number) => {
    if (formData.sharePointLinks.length > 1) {
      const newLinks = formData.sharePointLinks.filter((_, i) => i !== index);
      setFormData((prev) => ({
        ...prev,
        sharePointLinks: newLinks,
      }));
    }
  };

  const handleRouteGroupingToggle = (grouping: EmailReportGroupingOption) => {
    const isSelected = formData.routeGroupings.some((rg) => rg.id === grouping.id);

    if (isSelected) {
      setFormData((prev) => ({
        ...prev,
        routeGroupings: prev.routeGroupings.filter((rg) => rg.id !== grouping.id),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        routeGroupings: [...prev.routeGroupings, { id: grouping.id, name: grouping.name }],
      }));
    }
  };

  const validateStep1 = () => {
    if (!formData.reportName.trim()) {
      setError('Report name is required');
      return false;
    }

    const validRecipients = formData.recipients.filter((email) => email.trim());
    if (validRecipients.length === 0) {
      setError('At least one recipient email is required');
      return false;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const email of validRecipients) {
      if (!emailRegex.test(email)) {
        setError(`Invalid email address: ${email}`);
        return false;
      }
    }

    if (formData.routeGroupings.length === 0) {
      setError('At least one route grouping must be selected');
      return false;
    }

    if (formData.dateRangeType === 'survey_month') {
      if (
        !formData.surveyMonthStartDay ||
        formData.surveyMonthStartDay < 1 ||
        formData.surveyMonthStartDay > 31
      ) {
        setError('Survey month start day must be between 1 and 31');
        return false;
      }
    }

    return true;
  };

  const validateStep2 = () => {
    if (scheduleData.isActive) {
      if (
        !scheduleData.scheduleTime ||
        !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(scheduleData.scheduleTime)
      ) {
        setError('Valid schedule time (HH:MM format) is required when scheduling is enabled');
        return false;
      }
    }
    return true;
  };

  const loadPreview = async () => {
    if (!report) {
      setError("Cannot preview report that hasn't been created yet. Please save the report first.");
      return;
    }

    try {
      setLoading(true);
      const response = await api.emailReports.preview(report.id);
      setPreviewData(response.data as EmailReportPreview);
      setError(null);
    } catch (err) {
      console.error('Error loading preview:', err);
      setError((err as EmailReportApiError).response?.data?.error || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    setError(null);

    if (currentStep === 1) {
      if (!validateStep1()) return;
      setCurrentStep(2);
    } else if (currentStep === 2) {
      if (!validateStep2()) return;
      setCurrentStep(3);
      if (report) {
        await loadPreview();
      }
    }
  };

  const handleBack = () => {
    setError(null);
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  const handleSubmit = async () => {
    setError(null);

    if (!validateStep1() || !validateStep2()) {
      return;
    }

    try {
      setLoading(true);

      // Clean up data
      const submitData = {
        reportName: formData.reportName.trim(),
        description: formData.description.trim(),
        dateRangeType: formData.dateRangeType,
        surveyMonthStartDay:
          formData.dateRangeType === 'survey_month' ? formData.surveyMonthStartDay : null,
        recipients: formData.recipients.filter((email) => email.trim()),
        routeGroupings: formData.routeGroupings,
        sharePointLinks: formData.sharePointLinks.filter(
          (link) => link.url.trim() && link.text.trim()
        ),
        isActive: formData.isActive,
      };

      let response;
      if (report) {
        // Update existing report
        response = await api.emailReports.update(report.id, submitData);
      } else {
        // Create new report
        response = await api.emailReports.create(submitData);
      }

      const savedReport = response.data as EmailReportListRow;

      // The schedule: saved when ticked, switched off when unticked on a report whose
      // schedule is active (RPT-11: unticking used to leave it sending). The report itself is
      // saved either way, so a failed schedule call is reported and the modal still closes.
      const { schedule, error: scheduleError } = await saveReportSchedule(
        savedReport.id,
        scheduleData,
        !!report?.schedule?.is_active
      );
      if (scheduleError) {
        console.error('Error saving schedule:', scheduleError);
        toast.error(`The report was saved, but its schedule was not: ${scheduleError}`);
      }

      onSave(mergeScheduleIntoReport(savedReport, schedule));
      onClose();
    } catch (err) {
      console.error('Error saving report:', err);
      setError((err as EmailReportApiError).response?.data?.error || 'Failed to save report');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {steps.map((step, index) => (
        <div key={step.number} className="flex items-center">
          <div
            className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
              currentStep >= step.number
                ? 'bg-primary-500 border-primary-500 text-white'
                : 'border-gray-300 text-gray-400'
            }`}
          >
            {currentStep > step.number ? (
              <FaCheck className="w-5 h-5" />
            ) : (
              <step.icon className="w-5 h-5" />
            )}
          </div>
          <div className="ml-3">
            <div
              className={`text-sm font-medium ${
                currentStep >= step.number ? 'text-primary-600' : 'text-gray-400'
              }`}
            >
              Step {step.number}
            </div>
            <div
              className={`text-xs ${
                currentStep >= step.number ? 'text-gray-900' : 'text-gray-400'
              }`}
            >
              {step.title}
            </div>
          </div>
          {index < steps.length - 1 && (
            <div
              className={`w-12 h-0.5 mx-4 ${
                currentStep > step.number ? 'bg-primary-500' : 'bg-gray-300'
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      {/* Basic Settings */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Basic Settings</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Report Name *</label>
            <input
              type="text"
              value={formData.reportName}
              onChange={(e) => handleInputChange('reportName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter report name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date Range Type *
            </label>
            <ThemedSelect
              value={formData.dateRangeType}
              onChange={(e) => handleInputChange('dateRangeType', String(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="month_to_yesterday">First day of month to yesterday</option>
              <option value="survey_month">Survey month dates</option>
            </ThemedSelect>
          </div>
        </div>

        {formData.dateRangeType === 'survey_month' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Survey Month Start Day *
            </label>
            <input
              type="number"
              min="1"
              max="31"
              value={formData.surveyMonthStartDay}
              onChange={(e) => handleInputChange('surveyMonthStartDay', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            rows={3}
            placeholder="Optional description"
          />
        </div>
      </div>

      {/* Recipients */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Recipients</h3>
        {formData.recipients.map((recipient, index) => (
          <div key={index} className="flex items-center space-x-2">
            <input
              type="email"
              value={recipient}
              onChange={(e) => handleRecipientChange(index, e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter email address"
            />
            {formData.recipients.length > 1 && (
              <button
                type="button"
                onClick={() => removeRecipient(index)}
                className="text-red-600 hover:text-red-800 p-2"
              >
                <FaTrash />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={addRecipient}
          className="text-primary-600 hover:text-primary-800 flex items-center space-x-2"
        >
          <FaPlus />
          <span>Add Recipient</span>
        </button>
      </div>

      {/* Route Groupings */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Route Groupings *</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-4">
          {availableGroupings.map((grouping) => (
            <label key={grouping.id} className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.routeGroupings.some((rg) => rg.id === grouping.id)}
                onChange={() => handleRouteGroupingToggle(grouping)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">{grouping.name}</span>
            </label>
          ))}
        </div>
      </div>

      {/* SharePoint Links */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">SharePoint Links</h3>
        {formData.sharePointLinks.map((link, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="url"
              value={link.url}
              onChange={(e) => handleSharePointLinkChange(index, 'url', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="SharePoint URL"
            />
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={link.text}
                onChange={(e) => handleSharePointLinkChange(index, 'text', e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Link text"
              />
              {formData.sharePointLinks.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeSharePointLink(index)}
                  className="text-red-600 hover:text-red-800 p-2"
                >
                  <FaTrash />
                </button>
              )}
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={addSharePointLink}
          className="text-primary-600 hover:text-primary-800 flex items-center space-x-2"
        >
          <FaPlus />
          <span>Add SharePoint Link</span>
        </button>
      </div>

      {/* Active Status */}
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="isActive"
          checked={formData.isActive}
          onChange={(e) => handleInputChange('isActive', e.target.checked)}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
          Report is active
        </label>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">Schedule Settings</h3>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-3">
          <input
            type="checkbox"
            id="enableSchedule"
            checked={scheduleData.isActive}
            onChange={(e) => handleScheduleChange('isActive', e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="enableSchedule" className="text-sm font-medium text-gray-700">
            Enable automatic scheduling
          </label>
        </div>
        <p className="text-sm text-gray-600">
          When enabled, this report will be automatically sent to recipients based on the schedule
          below.
        </p>
      </div>

      {scheduleData.isActive && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Schedule Type *
              </label>
              <ThemedSelect
                value={scheduleData.scheduleType}
                onChange={(e) =>
                  handleScheduleChange('scheduleType', String(e.target.value) as ScheduleType)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </ThemedSelect>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Time *</label>
              <ThemedTimePicker
                value={scheduleData.scheduleTime}
                onChange={(e) => handleScheduleChange('scheduleTime', e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
            <ThemedSelect
              value={scheduleData.timezone}
              onChange={(e) => handleScheduleChange('timezone', String(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="Africa/Johannesburg">Africa/Johannesburg (SAST)</option>
              <option value="UTC">UTC</option>
              <option value="America/New_York">America/New_York (EST/EDT)</option>
              <option value="Europe/London">Europe/London (GMT/BST)</option>
            </ThemedSelect>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Schedule Summary</h4>
            <p className="text-sm text-gray-600">
              This report will be sent <strong>{scheduleData.scheduleType}</strong> at{' '}
              <strong>{scheduleData.scheduleTime}</strong> ({scheduleData.timezone})
            </p>
          </div>
        </div>
      )}

      {!scheduleData.isActive && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-600">
            Scheduling is disabled. The report can only be sent manually using the "Send Now"
            button.
          </p>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">Preview & Confirm</h3>

      {/* Report Summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Report Summary</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-700">Name:</span> {formData.reportName}
          </div>
          <div>
            <span className="font-medium text-gray-700">Date Range:</span> {formData.dateRangeType}
          </div>
          <div>
            <span className="font-medium text-gray-700">Recipients:</span>{' '}
            {formData.recipients.filter((r) => r.trim()).length}
          </div>
          <div>
            <span className="font-medium text-gray-700">Route Groupings:</span>{' '}
            {formData.routeGroupings.length}
          </div>
          <div>
            <span className="font-medium text-gray-700">Schedule:</span>{' '}
            {scheduleData.isActive
              ? `${scheduleData.scheduleType} at ${scheduleData.scheduleTime}`
              : 'Manual only'}
          </div>
          <div>
            <span className="font-medium text-gray-700">Status:</span>{' '}
            {formData.isActive ? 'Active' : 'Inactive'}
          </div>
        </div>
      </div>

      {/* Email Preview */}
      {report && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-900">Email Preview</h4>
            <button
              type="button"
              onClick={loadPreview}
              disabled={loading}
              className="text-primary-600 hover:text-primary-800 text-sm flex items-center space-x-1"
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaEye />}
              <span>{loading ? 'Loading...' : 'Refresh Preview'}</span>
            </button>
          </div>

          {previewData ? (
            <div className="border border-gray-200 rounded-lg">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                <div className="text-sm font-medium text-gray-900">
                  Subject: {previewData.subject}
                </div>
              </div>
              <div className="p-4 max-h-96 overflow-y-auto">
                <div dangerouslySetInnerHTML={{ __html: previewData.html }} />
              </div>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg p-8 text-center text-gray-500">
              {report
                ? 'Click "Refresh Preview" to see email content'
                : 'Preview will be available after saving the report'}
            </div>
          )}
        </div>
      )}

      {!report && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            <strong>Note:</strong> Email preview will be available after the report is created. You
            can edit the report later to see the preview.
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-2xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">
            {report ? 'Edit Email Report' : 'Create Email Report'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2">
            <FaTimes />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="p-6 border-b">{renderStepIndicator()}</div>

        {/* Content */}
        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {/* Step Content */}
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center p-6 border-t bg-gray-50">
          <div>
            {currentStep > 1 && (
              <button
                type="button"
                onClick={handleBack}
                className="px-4 py-2 text-gray-700 bg-surface border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors flex items-center space-x-2"
                disabled={loading}
              >
                <FaArrowLeft />
                <span>Back</span>
              </button>
            )}
          </div>

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-surface border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
              disabled={loading}
            >
              Cancel
            </button>

            {currentStep < 3 ? (
              <button
                type="button"
                onClick={handleNext}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors flex items-center space-x-2"
                disabled={loading}
              >
                <span>Next</span>
                <FaArrowRight />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2"
                disabled={loading}
              >
                {loading && <FaSpinner className="animate-spin" />}
                <span>{loading ? 'Saving...' : report ? 'Update Report' : 'Create Report'}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportConfigModal;
